import { useState, useEffect, useRef } from 'react'
import { parametresApi } from '../api'
import { useApp } from '../AppContext'
import { FormGroup, Loading } from '../components/ui'

export default function Parametres() {
  const { toast } = useApp()
  const [form, setForm] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }))

  const fileRef = useRef(null)
  const [logoPreview, setLogoPreview] = useState(form.logo_path || null)
  const [logoSaving, setLogoSaving] = useState(false)

  async function uploadLogo(e) {
    const file = e.target.files[0]
    if (!file) return
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast('Format accepté : JPG, PNG, WEBP', 'error'); return
    }
    if (file.size > 2 * 1024 * 1024) {
      toast('Logo trop lourd — max 2 Mo', 'error'); return
    }
    setLogoSaving(true)
    try {
      const data = new FormData()
      data.append('logo', file)
      const res = await fetch('/api/parametres/logo', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('hs_token')}` },
        body: data,
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.message)
      setForm(p => ({ ...p, logo_path: json.data.logo_path }))
      setLogoPreview(json.data.logo_url)
      toast('Logo enregistré', 'success')
    } catch (err) { toast(err.message, 'error') }
    finally { setLogoSaving(false) }
  }

  function removeLogo() {
    setForm(p => ({ ...p, logo_path: null }))
    setLogoPreview(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  useEffect(() => {
    parametresApi.get()
      .then(r => {
        const user = r.data.user || {}
        setForm(user)
        if (user.logo_path) {
          setLogoPreview(window.location.origin + '/api/' + user.logo_path)
        }
      })
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }, [])

  async function save(e) {
    e.preventDefault(); setSaving(true)
    try { await parametresApi.update(form); toast('Paramètres enregistrés', 'success') }
    catch (err) { toast(err.message, 'error') }
    finally { setSaving(false) }
  }

  if (loading) return <div className="content"><Loading /></div>

  return (
    <div className="content">
      <form onSubmit={save}>

        {/* Logo */}
        <FormGroup label="Logo de la société">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
            {logoPreview ? (
              <div style={{ position: 'relative' }}>
                <img
                  src={logoPreview}
                  alt="Logo"
                  style={{ height: 60, maxWidth: 160, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 6, background: '#fff' }}
                />
                <button
                  type="button"
                  onClick={removeLogo}
                  style={{ position: 'absolute', top: -8, right: -8, width: 20, height: 20, borderRadius: '50%', background: 'var(--danger)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <i className="ti ti-x" />
                </button>
              </div>
            ) : (
              <div style={{ width: 80, height: 60, border: '2px dashed var(--border2)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 12 }}>
                {form.logo_path
                  ? <img src={window.location.origin + '/api/' + form.logo_path} alt="Logo" style={{ maxHeight: 56, maxWidth: 74, objectFit: 'contain' }} />
                  : <i className="ti ti-photo" style={{ fontSize: 22 }} />
                }
              </div>
            )}
            <div>
              <button
                type="button"
                className="btn sm"
                onClick={() => fileRef.current?.click()}
                disabled={logoSaving}
              >
                <i className="ti ti-upload" /> {logoSaving ? 'Envoi…' : 'Choisir un logo'}
              </button>
              <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>JPG, PNG ou WEBP — max 2 Mo</p>
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={uploadLogo} />
        </FormGroup>

        <div className="grid2" style={{ gap: 18, alignItems: 'start' }}>

          {/* Infos société */}
          <div className="card">
            <div className="card-title">
              <span><i className="ti ti-building" style={{ marginRight: 6 }} />Informations société</span>
            </div>
            <div className="grid2">
              <FormGroup label="Nom">
                <input className="form-control" value={form.nom || ''} onChange={f('nom')} />
              </FormGroup>
              <FormGroup label="Prénom">
                <input className="form-control" value={form.prenom || ''} onChange={f('prenom')} />
              </FormGroup>
            </div>
            <FormGroup label="Raison sociale">
              <input className="form-control" value={form.societe || ''} onChange={f('societe')} />
            </FormGroup>
            <FormGroup label="Activité / Sous-titre">
              <input className="form-control" value={form.activite || ''} onChange={f('activite')} placeholder="Ex: Artisan plombier, SARL Informatique…" />
            </FormGroup>
            <div className="grid2">
              <FormGroup label="SIRET">
                <input className="form-control" value={form.siret || ''} onChange={f('siret')} />
              </FormGroup>
              <FormGroup label="N° TVA intra.">
                <input className="form-control" value={form.tva_intracommunautaire || ''} onChange={f('tva_intracommunautaire')} />
              </FormGroup>
            </div>
            <FormGroup label="Adresse">
              <input className="form-control" value={form.adresse || ''} onChange={f('adresse')} />
            </FormGroup>
            <div className="grid2">
              <FormGroup label="Code postal">
                <input className="form-control" value={form.code_postal || ''} onChange={f('code_postal')} />
              </FormGroup>
              <FormGroup label="Ville">
                <input className="form-control" value={form.ville || ''} onChange={f('ville')} />
              </FormGroup>
            </div>
            <div className="grid2">
              <FormGroup label="Téléphone">
                <input className="form-control" value={form.tel || ''} onChange={f('tel')} />
              </FormGroup>
              <FormGroup label="Email (documents)">
                <input type="email" className="form-control" value={form.email_contact || ''} onChange={f('email_contact')} />
              </FormGroup>
            </div>
            <FormGroup label="Site web">
              <input className="form-control" value={form.site_web || ''} onChange={f('site_web')} />
            </FormGroup>
          </div>

          {/* Facturation */}
          <div className="card">
            <div className="card-title">
              <span><i className="ti ti-file-invoice" style={{ marginRight: 6 }} />Paramètres de facturation</span>
            </div>
            <div className="grid2">
              <FormGroup label="Préfixe devis">
                <input className="form-control" value={form.prefixe_devis || 'D'} onChange={f('prefixe_devis')} />
              </FormGroup>
              <FormGroup label="Préfixe facture">
                <input className="form-control" value={form.prefixe_facture || 'F'} onChange={f('prefixe_facture')} />
              </FormGroup>
            </div>
            <div className="grid2">
              <FormGroup label="Préfixe bon commande">
                <input className="form-control" value={form.prefixe_commande || 'BC'} onChange={f('prefixe_commande')} />
              </FormGroup>
              <FormGroup label="Préfixe bon livraison">
                <input className="form-control" value={form.prefixe_livraison || 'BL'} onChange={f('prefixe_livraison')} />
              </FormGroup>
            </div>
            <div className="grid2">
              <FormGroup label="TVA par défaut (%)">
                <input type="number" className="form-control" value={form.taux_tva_defaut || 20} onChange={f('taux_tva_defaut')} min={0} step="any" />
              </FormGroup>
              <FormGroup label="Délai paiement (jours)">
                <input type="number" className="form-control" value={form.delai_paiement || 30} onChange={f('delai_paiement')} min={0} />
              </FormGroup>
            </div>
            <FormGroup label="Devise">
              <select className="form-control" value={form.monnaie || 'EUR'} onChange={f('monnaie')}>
                <option value="EUR">EUR — Euro</option>
                <option value="USD">USD — Dollar</option>
                <option value="XOF">XOF — Franc CFA</option>
                <option value="GBP">GBP — Livre sterling</option>
              </select>
            </FormGroup>
            <FormGroup label="Mention TVA">
              <input className="form-control" value={form.mention_tva || ''} onChange={f('mention_tva')} placeholder="Ex: TVA non applicable art.293B" />
            </FormGroup>
            <FormGroup label="Pied de page documents">
              <textarea className="form-control" rows={3} value={form.pied_de_page || ''} onChange={f('pied_de_page')} placeholder="Mentions légales, RIB, conditions…" />
            </FormGroup>
            <FormGroup label="Mentions obligatoires (bas de document)">
              <textarea className="form-control" rows={3} value={form.mentions_obligatoires || ''} onChange={f('mentions_obligatoires')} placeholder="SARL au capital de… N° Siret… RCS…" />
            </FormGroup>
            <FormGroup label="Information supplémentaire">
              <textarea className="form-control" rows={2} value={form.info_supplementaire || ''} onChange={f('info_supplementaire')} placeholder="Adhérent à un centre de gestion agréé…" />
            </FormGroup>
          </div>
        </div>

        {/* Options PDF */}
        <div className="card" style={{ marginTop: 18 }}>
          <div className="card-title">
            <span><i className="ti ti-file-settings" style={{ marginRight: 6 }} />Options des documents PDF</span>
          </div>
          <div className="grid3">
            {/* Layout */}
            <FormGroup label="Layout document">
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { v: 'A', label: 'A', title: 'Moderne — bandeau coloré' },
                  { v: 'D', label: 'D', title: 'Classique — sobre, sans bandeau' },
                ].map(l => (
                  <button
                    key={l.v}
                    type="button"
                    title={l.title}
                    onClick={() => setForm(p => ({ ...p, pdf_layout: l.v }))}
                    style={{
                      flex: 1, padding: '8px', border: `2px solid ${form.pdf_layout === l.v ? 'var(--primary)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius)', background: form.pdf_layout === l.v ? 'var(--primary-light)' : 'var(--bg)',
                      color: form.pdf_layout === l.v ? 'var(--primary)' : 'var(--text3)',
                      fontWeight: form.pdf_layout === l.v ? 700 : 400, cursor: 'pointer', fontSize: 14,
                    }}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                A : moderne (bandeau coloré) — D : classique, sobre (format proposé par le client)
              </p>
            </FormGroup>

            {/* Couleur */}
            <FormGroup label="Couleur des documents">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { v: 'vert', label: 'Vert', hex: '#2e7d52' },
                  { v: 'gris', label: 'Gris', hex: '#546e7a' },
                  { v: 'bleu', label: 'Bleu', hex: '#1565c0' },
                  { v: 'lavande', label: 'Lavande', hex: '#5e35b1' },
                  { v: 'jaune', label: 'Jaune', hex: '#f9a825' },
                ].map(c => (
                  <button
                    key={c.v}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, pdf_couleur: c.v }))}
                    title={c.label}
                    style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: c.hex,
                      border: form.pdf_couleur === c.v ? `3px solid var(--text)` : '3px solid transparent',
                      cursor: 'pointer', outline: form.pdf_couleur === c.v ? `2px solid ${c.hex}` : 'none',
                      outlineOffset: 2,
                    }}
                  />
                ))}
              </div>
            </FormGroup>

            {/* Décimales */}
            <FormGroup label="Décimales sur les montants">
              <div style={{ display: 'flex', gap: 8 }}>
                {[1, 2].map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, pdf_decimales: d }))}
                    style={{
                      flex: 1, padding: '8px', border: `2px solid ${form.pdf_decimales == d ? 'var(--primary)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius)', background: form.pdf_decimales == d ? 'var(--primary-light)' : 'var(--bg)',
                      color: form.pdf_decimales == d ? 'var(--primary)' : 'var(--text3)',
                      fontWeight: form.pdf_decimales == d ? 700 : 400, cursor: 'pointer', fontSize: 13,
                    }}
                  >
                    {d === 1 ? '0,0' : '0,00'}
                  </button>
                ))}
              </div>
            </FormGroup>
          </div>
        </div>

        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" className="btn primary" disabled={saving}>
            <i className="ti ti-device-floppy" />
            {saving ? 'Enregistrement…' : 'Enregistrer les paramètres'}
          </button>
        </div>
      </form>
    </div>
  )
}